"use client";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, Users, Target, Trophy } from "lucide-react";
import Link from "next/link";
import { cn } from "@/lib/utils";

const najcastejsieHraci = [
  { meno: "Juraj Slafkovský", pozicia: "Útočník", klub: "Montreal Canadiens", goly: 15 },
  { meno: "Šimon Nemec", pozicia: "Obranca", klub: "New Jersey Devils", goly: 8 },
  { meno: "Marek Hrivík", pozicia: "Útočník", klub: "HC Lausanne", goly: 12 },
  { meno: "Lukáš Cingel", pozicia: "Útočník", klub: "Kometa Brno", goly: 18 },
  { meno: "Adam Liška", pozicia: "Útočník", klub: "HC Bílí Tygři Liberec", goly: 14 },
];

const POROVNANIE_SEKCE = [
  {
    ikona: Trophy,
    titulok: "Porovnanie s oficiálnou súpiskou",
    popis: "Zistite, ako sa vaša nominácia porovnáva s oficiálnou súpiskou SZĽH pre ZOH 2026.",
    odkaz: "/oficialna-nominacia-zoh-2026",
    farba: "bg-primary/10 text-primary border-primary/20"
  },
  {
    ikona: TrendingUp,
    titulok: "Forma hráčov v NHL",
    popis: "Sledujte aktuálnu formu slovenských hráčov v najlepšej hokejovej lige sveta.",
    odkaz: "/nhl-statistiky",
    farba: "bg-green-500/10 text-green-600 border-green-500/20"
  },
  {
    ikona: Users,
    titulok: "Komunitné výsledky",
    popis: "Pozrite si, koho volia ostatní fanúšikov do svojich nominácií.",
    odkaz: "/vysledky",
    farba: "bg-blue-500/10 text-blue-600 border-blue-500/20"
  },
  {
    ikona: Target,
    titulok: "Analýza zostavy",
    popis: "Podrobná analýza štatistík a vyváženosti vašej nominácie.",
    odkaz: "/analyza",
    farba: "bg-orange-500/10 text-orange-600 border-orange-500/20"
  }
];

export function KomunitneSekcie() {
  return (
    <section className="py-16 bg-muted/20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <Badge variant="outline" className="mb-4 px-4 py-1.5 bg-primary/5 text-primary border-primary/20 rounded-full text-xs font-black uppercase tracking-widest">
            Komunita
          </Badge>
          <h2 className="text-3xl font-black tracking-tighter text-foreground sm:text-4xl mb-4">
            Najčastejšie <span className="text-primary">vyberaní hráči</span>
          </h2>
          <p className="text-lg text-muted-foreground/80 max-w-2xl mx-auto leading-relaxed font-medium">
            Kto je obľúbencom fanúšikov? Pozrite si najčastejšie volených hráčov do nominácií.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          {/* Najčastejšie vyberaní hráči */}
          <Card className="border-muted/20 shadow-sm bg-transparent">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-xl">
                <TrendingUp className="h-5 w-5 text-primary" />
                Top 5 najčastejšie volených hráčov
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {najcastejsieHraci.map((hrac, index) => (
                  <div key={hrac.meno} className="flex items-center justify-between p-3 rounded-lg bg-muted/30 border border-muted/20">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary font-bold text-sm">
                        {index + 1}
                      </div>
                      <div>
                        <Link 
                          href={`/hrac/${hrac.meno.toLowerCase().replace(' ', '-')}`}
                          className="font-medium hover:text-primary transition-colors"
                        >
                          {hrac.meno}
                        </Link>
                        <div className="text-sm text-muted-foreground">
                          {hrac.pozicia} • {hrac.klub}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-primary">{hrac.goly}</div>
                      <div className="text-xs text-muted-foreground">gólov</div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Porovnania a odkazy */}
          <div className="space-y-4">
            {POROVNANIE_SEKCE.map((sekcia, index) => {
              const Ikona = sekcia.ikona;
              return (
                <Card key={index} className="border-muted/20 shadow-sm bg-transparent hover:shadow-md transition-all">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className={cn(
                        "w-12 h-12 rounded-xl flex items-center justify-center shrink-0",
                        sekcia.farba
                      )}>
                        <Ikona className="h-6 w-6" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-bold text-lg mb-2">{sekcia.titulok}</h3>
                        <p className="text-muted-foreground mb-3">{sekcia.popis}</p>
                        <Link 
                          href={sekcia.odkaz}
                          className="inline-flex items-center gap-2 text-primary hover:text-primary/80 transition-colors font-medium"
                        >
                          Zobraziť viac
                          <TrendingUp className="h-4 w-4" />
                        </Link>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        <div className="text-center">
          <p className="text-sm text-muted-foreground">
            Každé meno je ďalšie kľúčové slovo pre Google • Zvyšujte SEO hodnotu webu
          </p>
        </div>
      </div>
    </section>
  );
}
